/* eslint-disable @typescript-eslint/no-explicit-any */
// Lib
import React, { useRef, useState } from 'react';
import {
  GetStaticComponentProps,
  Item,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { tailwindVariants } from './IngredientAccordionTailwind';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { sendGTMEvent } from '@next/third-parties/google';
import { ComponentProps } from 'lib/component-props';
import { useI18n } from 'next-localization';
import { stripHtml } from 'lib/utils/regex';

import Container from '../Layout/DartContainer/DartContainer';
import graphQLClientFactory from 'lib/graphql-client-factory';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';

export type AccordionListProps = ComponentProps & { staticProps?: Result };

export type Ingredient = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
    definition: {
      value: string;
    };
  };
};

export type Role = {
  targetItem: {
    name: string;
    title: {
      value: string;
    };
    definition: {
      value: string;
    };
  };
};
export type Language = {
  name: string;
};
export type ActiveCountry = {
  name: string;
  item: {
    languageList: {
      targetItems: Language[];
    };
  };
  id: string;
};
export type FormulaIngredient = {
  name: string;
  ingredient: Ingredient;
  role: Role;
  allergen: {
    value: string;
  };
  activeIngredientCountryMapping: {
    targetItems: ActiveCountry[];
  };
};
export type Fragrance = {
  name: string;
  oil: {
    value: string;
  };
  fragrances: {
    targetItems: {
      name: string;
      title: {
        value: string;
      };
      allergen: {
        value: string;
      };
    }[];
  };
};
export type Result = {
  activeIngredients: FormulaIngredient[];
  otherIngredients: FormulaIngredient[];
  isRegistered: boolean;
  fragrances: Fragrance[];
};

export type FormulaIngredients = {
  item: {
    children: {
      total: string;
      results: FormulaIngredient[];
    };
  };
};

export type FormulaFragrances = {
  item: {
    children: {
      total: string;
      results: Fragrance[];
    };
  };
};

const IngredientAccordion = (props: AccordionListProps): JSX.Element => {
  const { activeIngredients, otherIngredients, isRegistered, fragrances } =
    props?.staticProps || {};
  const { sitecoreContext } = useSitecoreContext();
  const contentDetails = sitecoreContext?.contentDetails as any;

  // Refs for scrolling
  const accordionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const i18n = useI18n();
  const IngredientsHeadline = i18n.t('Ingredients') ? i18n.t('Ingredients') : 'Ingredients';
  const ActiveIngredientsAccordionTitle = i18n.t('ActiveIngredients')
    ? i18n.t('ActiveIngredients')
    : 'Active Ingredients';
  const OtherIngredientsAccordionTitle = i18n.t('OtherIngredients')
    ? i18n.t('OtherIngredients')
    : 'Other Ingredients';
  const FragranceAccordionTitle = i18n.t('Fragrance') ? i18n.t('Fragrance') : 'Fragrance';
  const FragranceOilAccordionTitle = i18n.t('FragranceOil')
    ? i18n.t('FragranceOil')
    : 'Fragrance Oil';
  const allergenContent = contentDetails?.allergenContent || '';
  const fragranceContent = contentDetails?.fragranceContent || '';
  const registeredProductContent = contentDetails?.registeredProductContent || '';

  // Accordion Sections - only if isRegistered is true
  const accordionSections: { name: string; data: any }[] = [
    { name: ActiveIngredientsAccordionTitle, data: activeIngredients },
    { name: OtherIngredientsAccordionTitle, data: otherIngredients },
  ];

  const {
    componentHeaderContentWrapper,
    componentTitle,
    panel,
    panelBody,
    panelBodyWrapper,
    panelHeader,
    panelTitle,
    panelTitleButton,
    panelWrapperReg,
    panelTitleIcon,
    panelTitleText,
    panelRoleText,
    panelWrapper,
    ingredientWrapper,
    backgroundBG,
    ingredientTitle,
    ingredientRole,
    ingredientDescription,
    fragranceDescription,
    ingredientDisclaimer,
    registeredProductRTE,
  } = tailwindVariants({
    // alignment: alignment as Alignment,
    isRegistered: isRegistered as boolean,
  });

  const {
    expandedFirstAccordionOnPageLoad: expandedFirstAccordionOnPageLoadParam,
    openMultipleAccordionAtSameTime,
  } = props?.params ?? {};

  const expandedFirstAccordionOnPageLoad = expandedFirstAccordionOnPageLoadParam ? 0 : -1;
  const [activeIndexes, setActiveIndexes] = useState<number[]>([expandedFirstAccordionOnPageLoad]);

  const handleAccordionClick = (index: number, name: string) => {
    setActiveIndexes((prevIndexes) => {
      let newIndexes: number[] = [];

      if (openMultipleAccordionAtSameTime) {
        // toggle logic
        if (prevIndexes.includes(index)) {
          newIndexes = prevIndexes.filter((i) => i !== index);
        } else {
          newIndexes = [...prevIndexes, index];
        }
      } else {
        newIndexes = prevIndexes.includes(index) ? [] : [index];
      }

      return newIndexes;
    });

    // Filter indexes for Accordion
    if (activeIndexes.includes(index)) {
      sendGTMEvent({
        event: 'accordion',
        type: 'close',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });
      setActiveIndexes(activeIndexes.filter((item) => item !== index));
    } else {
      sendGTMEvent({
        event: 'accordion',
        type: 'open',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });
    }

    const currentAccordion = accordionRefs.current[index];
    if (currentAccordion) {
      setTimeout(() => {
        const elementTop = currentAccordion.getBoundingClientRect().top + window.scrollY;

        // Prefer jumplinks height if present, else use header height
        const jumplinks = document.querySelector('#jump-links') as HTMLElement | null;
        const header = document.querySelector('header') as HTMLElement | null;

        const offset = jumplinks?.offsetHeight || header?.offsetHeight || 0;
        const newPosition = elementTop - offset;

        window.scrollTo({ top: newPosition, behavior: 'smooth' });
      }, 300);
    }
  };

  const id = props?.params?.RenderingIdentifier;

  return (
    <>
      <div
        className={backgroundBG()}
        data-component="authorable/ingredientaccordion"
        id={id ? id : undefined}
        tabIndex={-1}
      >
        <Container>
          <div data-component="authorable/ingredientaccordion" data-testid="accordion">
            <div className={panelWrapper()}>
              {/* Headline */}
              {!isRegistered && (
                <div className={panelHeader()}>
                  <div className={componentHeaderContentWrapper()}>
                    <div className={componentTitle()}>
                      <Text encode={false} field={{ value: IngredientsHeadline }} tag="h2" />
                    </div>
                  </div>
                </div>
              )}

              {isRegistered ? (
                <>
                  {/* Is Registered */}
                  {accordionSections.map((section, index) => (
                    <div
                      key={index}
                      ref={(el) => {
                        accordionRefs.current[index] = el;
                      }}
                      className={panel()}
                    >
                      {/* Accordion Header */}
                      <button className={panelTitleButton()}>
                        <RichTextA11yWrapper field={{ value: section.name }} />
                        {!isRegistered && (
                          <GoogleMaterialSymbol
                            className={panelTitleIcon()}
                            icon={activeIndexes.includes(index) ? 'expand_less' : 'expand_more'}
                          />
                        )}
                      </button>
                      {/* Accordion Content */}
                      <>
                        {section.name === 'Active Ingredients' && (
                          <>
                            {section.data?.length > 0 && (
                              <ul className={panelBodyWrapper()}>
                                {section.data.map((item: FormulaIngredient, i: number) => (
                                  <li className={panelBody()} key={i}>
                                    <div className={ingredientWrapper()}>
                                      <>
                                        <div className={ingredientTitle()}>
                                          <Text
                                            tag="h3"
                                            field={{
                                              value: item?.ingredient?.targetItem?.title?.value,
                                            }}
                                          />
                                          {item?.allergen?.value === '1' && '*'}
                                        </div>
                                      </>
                                      <div className={ingredientRole()}>
                                        <Text
                                          field={{
                                            value: item?.role?.targetItem?.title?.value,
                                          }}
                                        />
                                      </div>
                                      <div className={ingredientDescription()}>
                                        <Text
                                          field={{
                                            value: item?.role?.targetItem?.definition?.value,
                                          }}
                                        />
                                      </div>

                                      {item?.allergen?.value === '1' && (
                                        <div className={ingredientDisclaimer()}>
                                          <RichTextA11yWrapper
                                            field={{
                                              value: allergenContent,
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}

                        {section.name === 'Other Ingredients' && (
                          <>
                            {section.data?.length > 0 && (
                              <ul className={panelBodyWrapper()}>
                                {section.data.map((item: any, i: number) => (
                                  <li className={panelBody()} key={i}>
                                    <div className={ingredientWrapper()}>
                                      <div className={ingredientTitle()}>
                                        <Text
                                          tag="h3"
                                          field={{
                                            value: item?.ingredient?.targetItem?.title?.value,
                                          }}
                                        />
                                        {item?.allergen?.value === '1' && '*'}
                                      </div>
                                      <div className={ingredientRole()}>
                                        <Text
                                          field={{
                                            value: item?.role?.targetItem?.title?.value,
                                          }}
                                        />
                                      </div>
                                      <div className={ingredientDescription()}>
                                        <Text
                                          field={{
                                            value: item?.role?.targetItem?.definition?.value,
                                          }}
                                        />
                                      </div>
                                    </div>
                                    {item?.allergen?.value === '1' && (
                                      <div className={ingredientDisclaimer()}>
                                        <RichTextA11yWrapper
                                          field={{
                                            value: allergenContent,
                                          }}
                                        />
                                      </div>
                                    )}
                                  </li>
                                ))}
                                {/* Append Fragrances at the bottom */}
                                {fragrances && fragrances?.length > 0 && (
                                  <>
                                    {fragrances?.map((fragranceItem, fIndex) => (
                                      <div key={fIndex} className={panelBody()}>
                                        <div className={ingredientWrapper()}>
                                          <div className={ingredientTitle()}>
                                            <Text
                                              tag="h3"
                                              field={{
                                                value:
                                                  fragranceItem?.oil?.value !== ''
                                                    ? FragranceOilAccordionTitle
                                                    : FragranceAccordionTitle,
                                              }}
                                            />
                                          </div>
                                          <div className={ingredientRole()}>
                                            <Text
                                              field={{
                                                value: FragranceAccordionTitle,
                                              }}
                                            />
                                          </div>
                                          <div className={fragranceDescription()} key={fIndex}>
                                            {fragranceItem?.fragrances?.targetItems?.map(
                                              (target, tIndex) => {
                                                const isLast =
                                                  tIndex ===
                                                  fragranceItem?.fragrances?.targetItems?.length -
                                                    1;

                                                const isAllergen = target?.allergen?.value === '1';

                                                return (
                                                  <div className="pr-2" key={tIndex}>
                                                    <Text
                                                      field={{
                                                        value: target?.title?.value,
                                                      }}
                                                    />
                                                    {isAllergen ? '*; ' : !isLast ? '; ' : ''}
                                                  </div>
                                                );
                                              }
                                            )}
                                          </div>
                                          {(() => {
                                            // Flatten all target items across fragrances
                                            const allTargets = fragrances?.flatMap(
                                              (f) => f?.fragrances?.targetItems || []
                                            );

                                            // Find first allergen with value "1"
                                            const hasAllergen = allTargets?.some(
                                              (target) => target?.allergen?.value === '1'
                                            );

                                            if (hasAllergen) {
                                              return (
                                                <>
                                                  <div className={ingredientDisclaimer()}>
                                                    <RichTextA11yWrapper
                                                      field={{ value: allergenContent }}
                                                    />
                                                  </div>
                                                </>
                                              );
                                            }

                                            return null;
                                          })()}
                                          <div className={ingredientDisclaimer()}>
                                            <RichTextA11yWrapper
                                              field={{ value: fragranceContent }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </ul>
                            )}
                          </>
                        )}
                      </>
                      {/* )} */}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {/* Not Registered */}
                  {otherIngredients?.map((item: FormulaIngredient, index: number) => {
                    const { panelBodyWrapper } = tailwindVariants({
                      isOpen: activeIndexes.includes(index) as boolean,
                    });

                    return (
                      <React.Fragment key={index}>
                        <div
                          ref={(el) => {
                            el;
                            accordionRefs.current[index] = el;
                          }}
                          className={panel()}
                          data-id={index}
                          key={index}
                        >
                          <div className={panelTitle()}>
                            <button
                              aria-label={stripHtml(
                                item?.ingredient?.targetItem?.title?.value as string
                              )}
                              type="button"
                              className={panelTitleButton()}
                              onClick={() =>
                                handleAccordionClick(index, openMultipleAccordionAtSameTime)
                              }
                              aria-expanded={activeIndexes.includes(index)}
                            >
                              <div className={panelWrapperReg()}>
                                <div className={panelTitleText()}>
                                  <RichTextA11yWrapper
                                    field={{
                                      value: item?.ingredient?.targetItem?.title?.value,
                                    }}
                                  />
                                  {item?.allergen?.value === '1' && '*'}
                                </div>
                                <div className={panelRoleText()}>
                                  <RichTextA11yWrapper
                                    field={{ value: item?.role?.targetItem?.title?.value }}
                                  />
                                </div>
                              </div>

                              {!isRegistered && (
                                <GoogleMaterialSymbol
                                  className={panelTitleIcon()}
                                  icon={
                                    activeIndexes.includes(index) ? 'expand_less' : 'expand_more'
                                  }
                                />
                              )}
                            </button>
                          </div>
                          <div
                            id={`accordion-panell-${index}-${'id'}`}
                            aria-labelledby={`accordion-panell-${index}-${'id'}-label`}
                            aria-expanded={activeIndexes.includes(index)}
                            className={panelBodyWrapper()}
                          >
                            <RichTextA11yWrapper
                              field={{ value: item?.ingredient?.targetItem?.definition?.value }}
                            />
                            {item?.allergen?.value === '1' && (
                              <div className={ingredientDisclaimer()}>
                                <RichTextA11yWrapper
                                  field={{
                                    value: allergenContent,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              )}

              {/* fragrances */}
              {!isRegistered &&
                fragrances &&
                fragrances.length > 0 &&
                fragrances.map((item, fIndex) => {
                  const Title = item?.fragrances?.targetItems;
                  const isFragrance = fragrances.length > 0;

                  // Offset index starts AFTER otherIngredients
                  const fragranceIndex = (otherIngredients?.length ?? 0) + fIndex;

                  const { panelBodyWrapper } = tailwindVariants({
                    isOpen: activeIndexes.includes(fragranceIndex),
                    isRegistered: isRegistered as boolean,
                    isFragrance: isFragrance,
                  });

                  return (
                    <React.Fragment key={fragranceIndex}>
                      <div
                        ref={(el) => {
                          accordionRefs.current[fragranceIndex] = el;
                        }}
                        className={panel()}
                        data-id={fragranceIndex}
                      >
                        <div className={panelTitle()}>
                          <button
                            aria-label={stripHtml(FragranceAccordionTitle)}
                            type="button"
                            className={panelTitleButton()}
                            onClick={() =>
                              handleAccordionClick(fragranceIndex, openMultipleAccordionAtSameTime)
                            }
                            aria-expanded={activeIndexes.includes(fragranceIndex)}
                          >
                            <div className={panelWrapperReg()}>
                              <div className={panelTitleText()}>
                                <RichTextA11yWrapper
                                  field={{
                                    value:
                                      item?.oil?.value !== ''
                                        ? FragranceOilAccordionTitle
                                        : FragranceAccordionTitle,
                                  }}
                                />
                              </div>
                              <div className={panelRoleText()}>
                                <RichTextA11yWrapper field={{ value: FragranceAccordionTitle }} />
                              </div>
                            </div>
                            <GoogleMaterialSymbol
                              className={panelTitleIcon()}
                              icon={
                                activeIndexes.includes(fragranceIndex)
                                  ? 'expand_less'
                                  : 'expand_more'
                              }
                            />
                          </button>
                        </div>
                        <div
                          id={`accordion-panell-${fragranceIndex}-id`}
                          aria-labelledby={`accordion-panell-${fragranceIndex}-id-label`}
                          aria-expanded={activeIndexes.includes(fragranceIndex)}
                          className={panelBodyWrapper()}
                        >
                          <div className={fragranceDescription()}>
                            {Title?.map((title, tIndex) => {
                              const isLast = tIndex === Title.length - 1;
                              return (
                                <div className="pr-2" key={tIndex}>
                                  <Text field={{ value: title?.title?.value }} />
                                  {title?.allergen?.value === '1' ? '*; ' : !isLast ? '; ' : ''}
                                </div>
                              );
                            })}
                          </div>
                          {(() => {
                            // Flatten all target items across fragrances
                            const allTargets = fragrances?.flatMap(
                              (f) => f?.fragrances?.targetItems || []
                            );

                            // Find first allergen with value "1"
                            const hasAllergen = allTargets?.some(
                              (target) => target?.allergen?.value === '1'
                            );

                            if (hasAllergen) {
                              return (
                                <>
                                  <div className={ingredientDisclaimer()}>
                                    <RichTextA11yWrapper field={{ value: allergenContent }} />
                                  </div>
                                </>
                              );
                            }

                            return null;
                          })()}
                          <div className={ingredientDisclaimer()}>
                            <RichTextA11yWrapper field={{ value: fragranceContent }} />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
          </div>
        </Container>
      </div>

      {/* RTE Content Paragraph */}
      {isRegistered && (
        <div className={registeredProductRTE()}>
          <RichTextA11yWrapper field={{ value: registeredProductContent }} />
        </div>
      )}
    </>
  );
};
export default IngredientAccordion;

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const graphQLClient = graphQLClientFactory({});
  const site = layoutData?.sitecore?.context?.site?.name;
  if (site?.toLocaleLowerCase() !== 'wiscj') {
    return {
      staticProps: null,
    };
  }
  const formula =
    layoutData?.sitecore?.route?.fields?.productInformation &&
    'fields' in layoutData?.sitecore?.route?.fields?.productInformation
      ? (layoutData?.sitecore?.route?.fields?.productInformation?.fields?.formula as Item[])
      : null;
  const formulaId = formula && formula.length > 0 ? formula[0]?.id : '';
  const language = layoutData?.sitecore?.context?.language;
  const data = await graphQLClient.request<FormulaIngredients>(query, {
    path: formulaId,
    language: language,
  });

  const formulaIngredients = data && data?.item?.children?.results;

  let isActive = false;
  const result: Result = {
    activeIngredients: [],
    otherIngredients: [],
    isRegistered: false,
    fragrances: [],
  };

  if (formulaIngredients && formulaIngredients.length > 0) {
    for (const formulaIngredient of formulaIngredients) {
      const activeCountries = formulaIngredient?.activeIngredientCountryMapping?.targetItems;
      if (activeCountries && activeCountries.length > 0) {
        for (const country of activeCountries) {
          const countryData = await graphQLClient.request<ActiveCountry>(countryQuery, {
            path: country?.id,
            language: 'en',
          });
          const languageItems = countryData?.item?.languageList?.targetItems;
          isActive = false;
          for (const lang of languageItems) {
            if (language?.toLocaleLowerCase() === lang?.name?.toString().toLocaleLowerCase()) {
              isActive = true;
              break;
            }
          }
          if (isActive) {
            break;
          }
        }
        if (isActive) {
          result.isRegistered = true;
          result.activeIngredients.push(formulaIngredient);
        } else {
          result.otherIngredients.push(formulaIngredient);
        }
      } else {
        result.otherIngredients.push(formulaIngredient);
      }
    }
  }

  const fragranceData = await graphQLClient.request<FormulaFragrances>(fragranceQuery, {
    path: formulaId,
    language: language,
  });

  const formulaFragrances = fragranceData?.item?.children?.results ?? [];
  result.fragrances.push(...formulaFragrances);

  //console.log('fragranceData:', fragranceData);
  return {
    staticProps: result,
  };
};

const query = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{89BC1BBC-B4D7-4580-97DF-2B20E23E0124}"], first: 20) {
        total
        results {
          ... on FormulaIngredient {
            name
            allergen: field(name: "allergen") {
              value
            }
            ingredient: field(name: "ingredient") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            role: field(name: "role") {
              ... on LookupField {
                targetItem {
                  name
                  title: field(name: "title") {
                    value
                  }
                  definition: field(name: "definition") {
                    value
                  }
                }
              }
            }
            activeIngredientCountryMapping: field(name: "activeIngredientCountryMapping") {
              ... on MultilistField {
                targetItems {
                  ... on CountryName {
                    name
                    id
                    title: field(name: "title") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const fragranceQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      children(includeTemplateIDs: ["{9B396867-3CBF-4575-9917-0D674E3E8F33}"]) {
        total
        results {
          ... on FragranceList {
            name
            oil: field(name: "oil") {
              value
            }
            fragrances: field(name: "fragrances") {
              ... on MultilistField {
                targetItems {
                  ... on Fragrance {
                    name
                    title: field(name: "title") {
                      value
                    }
                    allergen: field(name: "allergen") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const countryQuery = /* GraphQL */ `
  query ($path: String!, $language: String!) {
    item(path: $path, language: $language) {
      name
      languageList: field(name: "languageList") {
        ... on MultilistField {
          targetItems {
            id
            name
          }
        }
      }
    }
  }
`;
